import React from "react";
import {
  Button,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VerifiedIcon from "@mui/icons-material/Verified";
import GppMaybeOutlinedIcon from "@mui/icons-material/GppMaybeOutlined";
import {
  CATEGORY_LABELS,
  IslamicQuote,
} from "../../../graphql/IslamicQuotes/types";
import { useVerseText } from "./quran";

type Props = {
  quote: IslamicQuote | null; // null → closed
  onClose: () => void;
  onEdit: () => void;
};

const ViewQuoteDialog: React.FC<Props> = ({ quote, onClose, onEdit }) => {
  // QURAN quotes store no text — resolve via cmz-backend Quran proxy by surah/ayah.
  const needsFetch = quote?.category === "QURAN" && !quote?.arabicText;
  const { data: verse } = useVerseText(
    needsFetch ? quote?.surahNumber : null,
    needsFetch ? quote?.ayahNumber : null
  );
  const arabic = quote?.arabicText || verse?.arabic || "";
  const translation = quote?.translation || verse?.english || "";

  return (
    <Dialog
      open={!!quote}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <div className="QV_dialogHeader">
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          {quote && (
            <Chip
              label={CATEGORY_LABELS[quote.category] || quote.category}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: 11 }}
            />
          )}
          {quote &&
            (quote.isVerified ? (
              <Stack direction="row" alignItems="center" gap={0.25}>
                <VerifiedIcon sx={{ fontSize: 16, color: "#16a34a" }} />
                <Typography variant="caption" sx={{ color: "#16a34a", fontWeight: 600 }}>
                  Verified
                </Typography>
              </Stack>
            ) : (
              <Stack direction="row" alignItems="center" gap={0.25}>
                <GppMaybeOutlinedIcon sx={{ fontSize: 16, color: "#d97706" }} />
                <Typography variant="caption" sx={{ color: "#d97706", fontWeight: 600 }}>
                  Unverified
                </Typography>
              </Stack>
            ))}
          {quote && !quote.isActive && (
            <Chip
              label="Inactive"
              size="small"
              sx={{ fontSize: 10, height: 20, bgcolor: "#f3f4f6", color: "#6b7280" }}
            />
          )}
        </Stack>
        <IconButton aria-label="close" size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent dividers sx={{ p: 3 }}>
        {quote && (
          <Stack gap={2}>
            {/* Full Arabic — no truncation here */}
            <div
              className="QV_arabic"
              style={{ textAlign: "center", fontSize: 30, lineHeight: 2 }}
            >
              {arabic || "…"}
            </div>

            {translation && (
              <Typography
                sx={{ textAlign: "center", fontSize: 16, lineHeight: 1.7, color: "#374151" }}
              >
                {translation}
              </Typography>
            )}

            {quote.transliteration && (
              <Typography
                variant="body2"
                fontStyle="italic"
                sx={{ textAlign: "center", color: "text.secondary" }}
              >
                {quote.transliteration}
              </Typography>
            )}

            <Typography
              variant="caption"
              sx={{ color: "#6366f1", fontWeight: 700, textAlign: "center" }}
            >
              {quote.reference}
            </Typography>

            {(quote.tags.length > 0 || quote.labels.length > 0) && (
              <>
                <Divider />
                <Stack direction="row" flexWrap="wrap" gap={0.75} justifyContent="center">
                  {quote.tags.map((t) => (
                    <Chip
                      key={`t-${t}`}
                      label={t}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: 11 }}
                    />
                  ))}
                  {quote.labels.map((l) => (
                    <Chip
                      key={`l-${l}`}
                      label={l}
                      size="small"
                      sx={{ fontSize: 11, bgcolor: "#eef2ff", color: "#4338ca" }}
                    />
                  ))}
                </Stack>
              </>
            )}

            <Stack direction="row" justifyContent="flex-end" mt={1}>
              <Button
                variant="outlined"
                startIcon={<EditOutlinedIcon />}
                onClick={onEdit}
                sx={{ borderRadius: 2 }}
              >
                Edit
              </Button>
            </Stack>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewQuoteDialog;
